import { formatDistanceToNow } from "date-fns";
import { ShieldCheck } from "lucide-react";
import { EmptyState, Tooltip } from "@/design/components";
import { formatDateTime } from "@/design/format";
import { useProjectGovernanceHistory } from "@/domain/hooks";
import type { GovernanceHistoryEvent } from "@/domain/api";

// ZEUS-135 — surfaces justification for governance actions (creation,
// direct edits, status changes, edit-review submit/approve/reject)
// alongside the existing activity log, without redesigning it (non-goal).
// Rendered as its own section in the project's Audit trail tab.

const ACTION_LABELS: Record<string, string> = {
  "project.created": "Project created",
  "project.edited": "Project edited",
  "project.status_changed": "Status changed",
  "project.edit_submitted": "Edit submitted for review",
  "project.edit_approved": "Edit approved",
  "project.edit_rejected": "Edit rejected",
};

function actionLabel(eventtype: string): string {
  return ACTION_LABELS[eventtype] ?? eventtype;
}

export function ProjectGovernanceHistory({ projectId }: { projectId: string }) {
  const events = useProjectGovernanceHistory(projectId);

  if (events.length === 0) {
    return (
      <EmptyState
        title="No governance history yet"
        body="Creation, edits, and status changes with their justifications will appear here."
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between rounded-md border border-line bg-subtle/60 px-4 py-2.5">
        <span className="flex items-center gap-2 text-xs text-mute">
          <ShieldCheck className="h-3.5 w-3.5 text-faint" />
          Governance actions and their stated justification.
        </span>
        <span className="text-xs text-faint">{events.length} recorded</span>
      </div>
      <ol className="overflow-hidden rounded-lg border border-line bg-card">
        {events.map((e: GovernanceHistoryEvent) => (
          <li key={e.id} className="border-b border-hairline px-4 py-3 last:border-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-ink">{actionLabel(e.eventtype)}</p>
                {e.justification && (
                  <p className="mt-1 text-sm text-mute">{e.justification}</p>
                )}
              </div>
              <Tooltip label={formatDateTime(e.createdat)}>
                <span className="shrink-0 text-xs text-faint">
                  {formatDistanceToNow(new Date(e.createdat), { addSuffix: true })}
                </span>
              </Tooltip>
            </div>
            {e.actorrole && (
              <p className="mt-1 text-xs text-faint capitalize">{e.actorrole.replace(/_/g, " ")}</p>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
